import trim from 'lodash/trim';
import { Component, SimpleChanges, OnChanges, AfterViewInit, Input, Output, EventEmitter, ViewChild, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { NgbAccordionDirective, NgbAccordionModule } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import {
  CustomComponent,
  IOutlet,
  IScheduleOverride,
  ngHelper,
  IOutletAccordionOptions,
  OUTLET_SCHEDULE_TYPES
} from '@cakeiteasy/web-utils';
import { getOutletActionState, IOutletActionState } from './outlet-action-state.helper';
import { UiAccordionOutletComponent } from './ui-accordion-outlet/ui-accordion-outlet.component';
import { UiEarliestPickUpComponent } from './ui-earliest-pick-up/ui-earliest-pick-up.component';

@Component({
  standalone: true,
  selector: 'ui-outlets-accordion',
  templateUrl: './ui-outlets-accordion.component.html',
  styleUrls: ['./ui-outlets-accordion.component.scss'],
  imports: [NgbAccordionModule, TranslateModule, UiAccordionOutletComponent, UiEarliestPickUpComponent],
})
export class UiOutletsAccordionComponent extends CustomComponent implements OnChanges, OnInit, AfterViewInit {
  @Input() outlets: IOutlet[];
  @Input() scheduleOverridesByOutletId: { [outletId: number]: IScheduleOverride[] } = {};
  @Input() disabledOutletIds: number[];
  @Input() expandedOutlet: IOutlet;
  @Input() isSeparatedView: boolean;
  @Input() showNext: boolean;
  @Input() nextText: string;
  @Input() isOutletSelectionStateEnabled = true;
  @Input() destinationPrefix = 'outletDestination_';
  @Input() outletAccordionOptions: IOutletAccordionOptions;
  @Input() scheduleType: string = OUTLET_SCHEDULE_TYPES.WORKING_DAYS;

  @Output() onNext = new EventEmitter<IOutlet>();
  @Output() onOutletChanged = new EventEmitter<IOutlet>();
  @Output() onScrollEmit = new EventEmitter<string>();

  @ViewChild('acc', { static: false }) accordion: NgbAccordionDirective;

  public panelIdPrefix = 'panel_';
  public scrollEmitter$ = new Subject<string>();
  public outletActionStateMap: { [key: number]: IOutletActionState } = {};

  constructor() {
    super();
    this._subscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    ngHelper.ifChanged(changes, 'expandedOutlet', (outlet) => {
      setTimeout(() => {
        if (outlet && this.accordion) {
          this.accordion.expand(this._getPanelPrefix(outlet.id));
        }
      });
    });
  }

  ngOnInit() {
    this._buildOutletActionStateMap();
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      if (this.outlets.length === 1 && !this.expandedOutlet && this.accordion) {
        this.accordion.expand(this._getPanelPrefix(this.outlets[0].id));
      }
    });
  }

  public outletChanged(panel) {
    setTimeout(() => {
      if (panel.nextState) {
        this.expandedOutlet = this.outlets.find((o) => o.id === this._getOutletIdFromPanelId(panel.panelId));
      } else {
        this.expandedOutlet = null;
      }

      this.onOutletChanged.emit(this.expandedOutlet);

      if (this.expandedOutlet) {
        this.scrollEmitter$.next(this.destinationPrefix + this._getOutletIdFromPanelId(panel.panelId));
      }
    });
  }

  public isPanelActive(panelId) {
    return this.expandedOutlet && this.expandedOutlet.id === this._getOutletIdFromPanelId(panelId);
  }

  private _getPanelPrefix(id: number) {
    return this.panelIdPrefix + id;
  }

  private _getOutletIdFromPanelId(panelId: string) {
    return +trim(panelId, this.panelIdPrefix);
  }

  private _buildOutletActionStateMap() {
    if (!this.outlets || this.outlets.length === 0) {
      this.outletActionStateMap = {};
      return;
    }

    const disabledOutletIds = this.disabledOutletIds || [];
    this.outletActionStateMap = this.outlets.reduce((acc, outlet) => {
      acc[outlet.id] = getOutletActionState({
        outlet, disabledOutletIds, isOutletSelectionStateEnabled: this.isOutletSelectionStateEnabled
      });
      return acc;
    }, {});
  }

  private _subscribe() {
    this.addSubscriber(this.scrollEmitter$.pipe(debounceTime(50))
      .subscribe((target) => this.onScrollEmit.emit(target)));
  }
}
